import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { MapPin, Calendar, Trash, Plus, Eye, Tag, PencilSimple } from '@phosphor-icons/react';
import { getCategoryLabel } from '../lib/categories';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MyListings() {
  const { user } = useAuth();
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchMySales = async () => {
    try {
      const { data } = await axios.get(`${API}/my-sales`, { withCredentials: true });
      setSales(data.sales || []);
    } catch (err) {
      toast.error('Failed to load your listings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchMySales(); }, []);

  const handleDelete = async (saleId) => {
    if (!window.confirm('Delete this listing?')) return;
    try {
      await axios.delete(`${API}/sales/${saleId}`, { withCredentials: true });
      setSales(prev => prev.filter(s => s.id !== saleId));
      toast.success('Listing deleted');
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  return (
    <div className="min-h-[calc(100vh-64px)] bg-[#F9F9F6] px-4 py-8" data-testid="my-listings-page">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl sm:text-4xl font-black text-[#004E89]" style={{ fontFamily: 'Outfit' }}>My Listings</h1>
            <p className="text-[#5C7A99] mt-1">Manage your estate and garage sales</p>
          </div>
          <Link to="/create" data-testid="my-listings-create-btn">
            <button className="btn-primary py-2.5 px-5 flex items-center gap-2">
              <Plus size={18} weight="bold" /> New Sale
            </button>
          </Link>
        </div>

        {user?.subscription === 'premium' && (
          <div className="bg-[#FFD166] border-2 border-[#004E89] rounded-xl p-4 mb-6 shadow-[4px_4px_0px_#004E89] flex items-center gap-3" data-testid="premium-badge">
            <Tag size={24} weight="fill" className="text-[#004E89]" />
            <div>
              <p className="font-bold text-[#004E89] text-sm">Pro Subscription Active</p>
              <p className="text-xs text-[#004E89] opacity-75">Unlimited listings and photos</p>
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-center py-20 text-[#5C7A99] font-medium">Loading your listings...</div>
        ) : sales.length === 0 ? (
          <div className="text-center py-20" data-testid="no-listings">
            <MapPin size={48} weight="duotone" className="mx-auto text-[#5C7A99] mb-4" />
            <p className="text-lg font-bold text-[#004E89] mb-2">No listings yet</p>
            <p className="text-[#5C7A99] mb-6">Create your first estate or garage sale listing</p>
            <Link to="/create">
              <button className="btn-primary py-3 px-6">Post Your First Sale</button>
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {sales.map(sale => (
              <div key={sale.id} className="bg-white border-2 border-[#004E89] rounded-2xl p-5 shadow-[4px_4px_0px_#004E89] flex gap-4 items-start hover:-translate-y-0.5 transition-transform" data-testid={`my-listing-${sale.id}`}>
                {/* Thumbnail */}
                <div className="w-24 h-24 rounded-xl overflow-hidden border-2 border-[#004E89] flex-shrink-0">
                  {sale.photos?.length > 0 ? (
                    <img src={`${API}/files/${sale.photos[0]}`} alt="" className="w-full h-full object-cover"
                      onError={(e) => { e.target.src = 'https://images.pexels.com/photos/4923440/pexels-photo-4923440.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940'; }} />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-[#FFD166] to-[#FF6B35] flex items-center justify-center">
                      <MapPin size={24} weight="duotone" color="#004E89" />
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`tag text-[10px] tag-${sale.category === 'business_closing' ? 'business' : sale.category}`}>
                      {getCategoryLabel(sale.category)}
                    </span>
                    <span className={`tag text-[10px] ${sale.status === 'active' ? 'bg-[#4CB944] text-white border-[#004E89]' : 'bg-gray-200 text-gray-600 border-gray-400'}`}>
                      {sale.status}
                    </span>
                  </div>
                  <h3 className="font-bold text-[#004E89] text-base truncate" style={{ fontFamily: 'Outfit' }}>{sale.title}</h3>
                  <p className="text-xs text-[#5C7A99] flex items-center gap-1 mt-1">
                    <MapPin size={12} weight="bold" /> {sale.city}, {sale.state}
                  </p>
                  <p className="text-xs text-[#5C7A99] flex items-center gap-1">
                    <Calendar size={12} weight="bold" /> {new Date(sale.start_date).toLocaleDateString()} - {new Date(sale.end_date).toLocaleDateString()}
                  </p>
                </div>

                {/* Actions */}
                <div className="flex gap-2 flex-shrink-0">
                  <Link to={`/edit-sale/${sale.id}`}>
                    <button className="btn-outline py-2 px-3 text-xs" data-testid={`edit-listing-${sale.id}`}>
                      <PencilSimple size={16} weight="bold" />
                    </button>
                  </Link>
                  <Link to={`/sale/${sale.id}`}>
                    <button className="btn-outline py-2 px-3 text-xs" data-testid={`view-listing-${sale.id}`}>
                      <Eye size={16} weight="bold" />
                    </button>
                  </Link>
                  <button onClick={() => handleDelete(sale.id)} className="btn-outline py-2 px-3 text-xs text-red-600 border-red-400 shadow-[2px_2px_0px_#dc2626] hover:bg-red-50" data-testid={`delete-listing-${sale.id}`}>
                    <Trash size={16} weight="bold" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
